import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";

const IMAGES = {
  hero: "https://static.prod-images.emergentagent.com/jobs/05762e59-204f-4b94-afa0-9e5df23203e5/images/97a3acf562b1ab58ff93fe0cb1663703f78b65c354cbc046d061741598d0e7c0.png",
  diag: "https://static.prod-images.emergentagent.com/jobs/05762e59-204f-4b94-afa0-9e5df23203e5/images/6c81daee44b76a6531cb309d110a3a201b2f5990f9edeeda439e45b72fdb71d8.png",
  sop:  "https://static.prod-images.emergentagent.com/jobs/05762e59-204f-4b94-afa0-9e5df23203e5/images/2b2d0dc3113295748b68921b4631b57dfe0a7716ae2d878acf8d056307b1ccdc.png",
  opt:  "https://static.prod-images.emergentagent.com/jobs/05762e59-204f-4b94-afa0-9e5df23203e5/images/3fd79c05a7caa734f997b68b4b94e7876a57b19a0e50d3c86230a7c6f17ac7f7.png",
};

const CATEGORY_META = {
  diagnostic:  { label: "Diagnostic / 01", img: IMAGES.diag, blurb: "Async hardware troubleshooting with ranked root-cause analysis and step-by-step fix plans." },
  sop:         { label: "Documentation / 02", img: IMAGES.sop, blurb: "Turn messy tribal knowledge into clean, professional SOPs your team can follow." },
  optimization:{ label: "Optimization / 03", img: IMAGES.opt, blurb: "Identify hardware overspend, risk and reliability hotspots with savings estimates." },
};

export default function Landing() {
  const [services, setServices] = useState([]);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const groups = services.reduce((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s);
    return acc;
  }, {});

  const startOrder = (serviceId) => {
    if (!user) nav(`/signup?service=${serviceId}`);
    else nav(`/dashboard?buy=${serviceId}`);
  };

  return (
    <div data-testid="landing-page">
      <NavBar />

      {/* HERO — Tetris bento grid */}
      <section className="border-b border-black">
        <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-12 gap-6 lg:gap-10">
          <div className="col-span-12 lg:col-span-7">
            <div className="label-eyebrow mb-6">FILE //  AI-HW-001 ·  ASYNC ENGINEERING</div>
            <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-6xl lg:text-7xl leading-[0.92]"
                data-testid="hero-headline">
              Hardware issues.<br />
              Diagnosed fast.<br />
              <span style={{ color: "var(--c-primary)" }}>No calls required.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-black/80">
              Upload your problem. A senior hardware engineer + AI returns a professional
              fix plan, SOP or cost-optimization report in 24–72 hours. Fixed price.
              Async. Boring. That's the point.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href="#services" className="btn btn-primary" data-testid="hero-cta-services">
                Browse services →
              </a>
              <Link to={user ? "/dashboard" : "/signup"} className="btn btn-outline"
                    data-testid="hero-cta-start">
                {user ? "Open dashboard" : "Create account"}
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 border-t border-black/20 pt-6 max-w-xl">
              {[
                ["24–72H", "Turnaround"],
                ["0", "Calls required"],
                ["£79+", "Fixed pricing"],
              ].map(([k, v]) => (
                <div key={v}>
                  <div className="font-display text-3xl font-black">{k}</div>
                  <div className="ribbon text-black/60 mt-1">{v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <div className="relative h-full min-h-[420px] border border-black">
              <img src={IMAGES.hero} alt="Hardware lab"
                   className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.35)" }} />
              <div className="absolute top-4 left-4 right-4 ribbon text-white">
                LAB / 4019N · TEMP 22°C · OK
              </div>
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <div className="ribbon mb-2 text-white/80">CURRENT QUEUE</div>
                <div className="font-display text-2xl font-black uppercase leading-tight">
                  Reports shipping every business day
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <div className="bg-black text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-3 ribbon flex gap-12 whitespace-nowrap">
          <span>HARDWARE FAILURE ANALYSIS</span>
          <span>·</span>
          <span>SOP AUTHORING</span>
          <span>·</span>
          <span>FLEET COST OPTIMIZATION</span>
          <span>·</span>
          <span>EMBEDDED + SERVERS + PC</span>
          <span>·</span>
          <span>FIXED-PRICE · ASYNC</span>
        </div>
      </div>

      {/* SERVICES */}
      <section id="services" className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-12 gap-6 mb-12 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="label-eyebrow mb-3">SECTION / 02 ·  SERVICES</div>
            <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">
              Three productized<br />engineering services.
            </h2>
          </div>
          <p className="col-span-12 md:col-span-4 text-black/70">
            Pick a tier. Pay. Submit details. Receive a professional PDF-grade report. That's
            the entire workflow.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 grid-frame">
          {Object.entries(CATEGORY_META).map(([cat, meta]) => (
            <div key={cat} className="bg-white" data-testid={`service-card-${cat}`}>
              <div className="relative aspect-[4/3]">
                <img src={meta.img} alt={meta.label} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute top-3 left-3 ribbon bg-white px-2 py-1">{meta.label}</div>
              </div>
              <div className="p-6">
                <h3 className="font-display text-2xl font-black uppercase tracking-tight">
                  {cat === "diagnostic" && "Diagnostic Reports"}
                  {cat === "sop" && "SOP Documentation"}
                  {cat === "optimization" && "Cost Optimization"}
                </h3>
                <p className="text-sm text-black/70 mt-2 leading-relaxed">{meta.blurb}</p>
                <div className="mt-5 space-y-2">
                  {(groups[cat] || []).map((s) => (
                    <div key={s.id} className="flex items-center justify-between border border-black/15 p-3"
                         data-testid={`tier-row-${s.id}`}>
                      <div>
                        <div className="font-display font-bold text-sm uppercase tracking-tight">{s.name.split("—")[1]?.trim() || s.name}</div>
                        <div className="ribbon text-black/60 mt-1">{s.turnaround}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-lg font-black">£{s.price.toFixed(0)}</div>
                        <button onClick={() => startOrder(s.id)}
                                className="ribbon underline underline-offset-4 hover:text-[color:var(--c-primary)]"
                                data-testid={`buy-btn-${s.id}`}>
                          Order →
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-[color:var(--c-bg-alt)] border-y border-black">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="label-eyebrow mb-3">SECTION / 03 ·  PROCESS</div>
          <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight mb-12">
            From paid order to PDF<br /> in three boring steps.
          </h2>
          <div className="grid md:grid-cols-3 gap-0 grid-frame">
            {[
              ["01 / PAY", "Pick a tier. Pay via Stripe. Get instant access to the intake form."],
              ["02 / UPLOAD", "Submit symptoms, notes, logs and photos. Async — no scheduling, no calls."],
              ["03 / RECEIVE", "An engineer-grade report lands in your dashboard within 24–72 hours."],
            ].map(([t, d]) => (
              <div key={t} className="bg-white p-8" data-testid={`how-step-${t.slice(0,2)}`}>
                <div className="ribbon text-black/60 mb-4">{t}</div>
                <p className="font-display text-xl font-bold leading-snug">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING SUMMARY */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-12 gap-6 mb-10">
          <div className="col-span-12 md:col-span-7">
            <div className="label-eyebrow mb-3">SECTION / 04 ·  PRICING</div>
            <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">
              Fixed price.<br /> Boring on purpose.
            </h2>
          </div>
          <p className="col-span-12 md:col-span-5 text-black/70">
            No retainers. No hourly billing. No surprises. The numbers below are the
            numbers you will pay.
          </p>
        </div>
        <div className="overflow-x-auto border border-black">
          <table className="w-full text-left border-collapse" data-testid="pricing-table">
            <thead>
              <tr className="bg-black text-white">
                <th className="p-4 ribbon">Service</th>
                <th className="p-4 ribbon">Tier</th>
                <th className="p-4 ribbon">Turnaround</th>
                <th className="p-4 ribbon">Price</th>
                <th className="p-4 ribbon"></th>
              </tr>
            </thead>
            <tbody>
              {services.map((s) => (
                <tr key={s.id} className="border-t border-black/15" data-testid={`pricing-row-${s.id}`}>
                  <td className="p-4 font-mono text-sm uppercase">{s.category}</td>
                  <td className="p-4 font-display font-bold">{s.name.split("—")[1]?.trim() || s.name}</td>
                  <td className="p-4 ribbon">{s.turnaround}</td>
                  <td className="p-4 font-display font-black text-lg">£{s.price.toFixed(2)}</td>
                  <td className="p-4 text-right">
                    <button onClick={() => startOrder(s.id)} className="btn btn-sm btn-outline"
                            data-testid={`pricing-buy-${s.id}`}>Order</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="label-eyebrow mb-3 text-white/60">SECTION / 05 ·  FAQ</div>
          <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight mb-10">
            Frequently asked.<br /> Honestly answered.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
            {[
              ["Do I get on a call with anyone?", "No. Everything is async. You submit details through a form, you receive a written report. That's the entire interaction."],
              ["Is this generated only by AI?", "AI drafts the report from your inputs using a senior-engineer prompt. A human reviews and releases it before you can download. You get the benefit of speed plus a real engineer's judgement."],
              ["What happens if the report is wrong?", "Every report flags uncertainty and recommends validation steps. We never invent part numbers. Recommendations are advisory and should be tested in your environment first."],
              ["Can I get a refund?", "Refunds are not offered once a report has been generated and released. If a report is materially unusable we will revise it once at no cost."],
            ].map(([q, a]) => (
              <div key={q} data-testid={`faq-${q.slice(0,20)}`}>
                <h3 className="font-display text-xl font-bold uppercase tracking-tight">{q}</h3>
                <p className="mt-3 text-white/75 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20 text-center">
        <div className="label-eyebrow mb-3">SECTION / 06 ·  START</div>
        <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95] max-w-4xl mx-auto">
          Stop hoarding tribal knowledge.<br />
          <span style={{ color: "var(--c-primary)" }}>Ship the report.</span>
        </h2>
        <div className="mt-10 flex justify-center gap-3">
          <a href="#services" className="btn btn-primary" data-testid="bottom-cta-services">See services</a>
          <Link to={user ? "/dashboard" : "/signup"} className="btn btn-outline" data-testid="bottom-cta-signup">
            {user ? "Open dashboard" : "Get started"}
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
